import { useReducer } from 'react';

import {
  ACTION_TYPE,
  MakeModel,
  UseMakeModelReducerProps,
} from '../../../../components/SearchPage/features/makeModel/MakeModel.typed';
import type {
  ActionType,
  Options,
  StateType,
} from '../../../../components/SearchPage/features/makeModel/MakeModel.typed';

import type { ListItem } from '../../../../components/Toolkit/Inputs/CustomSelect';

const makeModelReducer = (state: StateType, action: ActionType): StateType => {
  switch (action.type) {
    case ACTION_TYPE.SET_IS_EXPANDED:
      return {
        ...state,
        isExpanded: action.payload,
      };
    case ACTION_TYPE.SET_IS_MAKE_MODEL_BOTTOM_SHEET_OPEN:
      return {
        ...state,
        isMakeModelBottomSheetOpen: action.payload,
      };
    case ACTION_TYPE.HANDLE_CLOSE:
      return {
        ...state,
      };
    case ACTION_TYPE.CLEAR_MAKE_MODELS:
      return {
        ...state,
        makeModels: undefined,
        modelOptions: undefined,
      };
    case ACTION_TYPE.CLOSE_DISPLAY:
      return {
        ...state,
        isExpanded: false,
        isMakeModelBottomSheetOpen: false,
      };
    case ACTION_TYPE.ADD_MODEL_OPTIONS:
      return {
        ...state,
        modelOptions: action.payload,
        // TODO: Follow up task to unify this in MakeModel.map.ts
        // modelItems is a flat list of all the options
        // see ModelFilter.tsx for how this is used
        modelItems: action.payload?.flatMap(({ title, items }) =>
          items.map(({ displayName, value, count, isDisabled }, index) => ({
            value,
            displayName,
            group: (index === 0 && title) || undefined,
            count,
            isDisabled,
          })),
        ),
      };
    case ACTION_TYPE.ADD_MODEL_ITEMS:
      return {
        ...state,
        modelItems: action.payload,
      };
    case ACTION_TYPE.SET_MAKE_MODELS:
      return {
        ...state,
        makeModels: action.payload,
      };
    case ACTION_TYPE.ADD_TRIM_OPTIONS:
      return {
        ...state,
        trimOptions: action.payload,
        trimItems: action.payload?.flatMap(({ title, items }) =>
          items.map(({ displayName, value, count, isDisabled }, index) => ({
            value,
            displayName,
            group: (index === 0 && title) || undefined,
            count,
            isDisabled,
          })),
        ),
      };
    case ACTION_TYPE.ADD_TRIM_ITEMS:
      return {
        ...state,
        trimItems: action.payload,
      };
    case ACTION_TYPE.SET_MODELS_LOADING:
      return {
        ...state,
        modelsLoading: action.payload,
      };
    default:
      return state;
  }
};

const initialState: StateType = {
  isExpanded: false,
  isMakeModelBottomSheetOpen: false,
  modelsLoading: true,
};

const useMakeModelReducer = (props: UseMakeModelReducerProps) => {
  const { initialMakeModels } = props;

  const [state, dispatch] = useReducer(makeModelReducer, {
    ...initialState,
    makeModels: initialMakeModels,
  });

  const setIsExpanded = (isExpanded: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_IS_EXPANDED,
      payload: isExpanded,
    });

  const setIsMakeModelBottomSheetOpen = (isMakeModelBottomSheetOpen: boolean) =>
    dispatch({
      type: ACTION_TYPE.SET_IS_MAKE_MODEL_BOTTOM_SHEET_OPEN,
      payload: isMakeModelBottomSheetOpen,
    });

  const handleClose = (areas: ListItem[]) =>
    dispatch({
      type: ACTION_TYPE.HANDLE_CLOSE,
      payload: areas,
    });

  const closeDisplay = () =>
    dispatch({
      type: ACTION_TYPE.CLOSE_DISPLAY,
    });

  const clearMakeModels = () => {
    dispatch({
      type: ACTION_TYPE.CLEAR_MAKE_MODELS,
    });
  };

  const addModelOptions = (models?: Array<Options>) => {
    dispatch({
      type: ACTION_TYPE.ADD_MODEL_OPTIONS,
      payload: models,
    });
  };

  const setModelItems = (modelItems: Array<ListItem>) => {
    dispatch({
      type: ACTION_TYPE.ADD_MODEL_ITEMS,
      payload: modelItems,
    });
  };

  const setMakeModels = (makeModels?: Array<MakeModel>) => {
    dispatch({
      type: ACTION_TYPE.SET_MAKE_MODELS,
      payload: makeModels,
    });
  };

  const addTrimOptions = (trims?: Array<Options>) => {
    dispatch({
      type: ACTION_TYPE.ADD_TRIM_OPTIONS,
      payload: trims,
    });
  };

  const setTrimItems = (trimItems: Array<ListItem>) => {
    dispatch({
      type: ACTION_TYPE.ADD_TRIM_ITEMS,
      payload: trimItems,
    });
  };

  const setModelsLoading = (loading: boolean) => {
    dispatch({
      type: ACTION_TYPE.SET_MODELS_LOADING,
      payload: loading,
    });
  };

  return {
    isExpanded: state.isExpanded,
    isMakeModelBottomSheetOpen: state.isMakeModelBottomSheetOpen,
    makeModels: state.makeModels,
    modelOptions: state.modelOptions,
    modelItems: state.modelItems,
    trimOptions: state.trimOptions,
    trimItems: state.trimItems,
    modelsLoading: state.modelsLoading,
    setIsExpanded,
    setIsMakeModelBottomSheetOpen,
    handleClose,
    clearMakeModels,
    closeDisplay,
    addModelOptions,
    setModelItems,
    addTrimOptions,
    setTrimItems,
    setMakeModels,
    setModelsLoading,
  };
};

export { useMakeModelReducer };
